import { nextTick, ref, useContext } from '@nuxtjs/composition-api';
import { extend } from 'vee-validate';
import { email, max, required } from 'vee-validate/dist/rules';
import VueScrollTo from 'vue-scrollto';
import { assign, clone, join, trim } from 'lodash-es';
import Utils from '~/helpers/utils';
import {
  ContactUsMutationResult,
  SendHydrogenLeadsNewsletterRequestMutationResult,
  SendHydrogenLeadsRequestMutationResult,
  useContact,
} from '~/composables/useContact';
import { useGtm } from '~/composables/useGtm';
import { getHydrogenCountryLabel } from '~/constants/hydrogenCountryOptions';

type HydrogenContactForm = {
  name: string;
  position: string;
  telephone: string;
  email: string;
  company: string;
  comment: string;
};

type HydrogenPersonalInfo = {
  lastName: string;
};

type HydrogenCompanyInfo = {
  country: string;
  city: string;
  postalCode: string;
  addressLine1?: string;
  addressLine2?: string;
};

type HydrogenContactFormContext = {
  form: HydrogenContactForm;
  personalInfo: HydrogenPersonalInfo;
  companyInfo: HydrogenCompanyInfo;
  communicationConsent: boolean;
  requestType: string;
  i18n: any;
};

type HydrogenContactFormOptions = {
  formName: string;
  subject: string;
  dynamicTitle?: string;
  leadSubSource?: string;
  maxCharacters?: number;
  requestTypeOptions?: string[];
  defaultRequestType?: string;
  getInitialForm?: () => HydrogenContactForm;
  getInitialCompanyInfo?: () => HydrogenCompanyInfo;
  buildComment: (context: HydrogenContactFormContext) => string;
  buildSubmissionData?: (context: HydrogenContactFormContext) => Record<string, unknown>;
  buildHydrogenLeadData?: (context: HydrogenContactFormContext) => Record<string, unknown>;
  onReset?: () => void;
};

const formTextFieldNames = ['name', 'position', 'telephone', 'email', 'company', 'comment'] as const;
type FormTextFieldName = typeof formTextFieldNames[number];

const isFormTextFieldName = (fieldName: string): fieldName is FormTextFieldName => (
  (formTextFieldNames as readonly string[]).includes(fieldName)
);

let areValidationRulesRegistered = false;

const registerValidationRules = (preventSqlAttacks: (value: string) => boolean) => {
  if (areValidationRulesRegistered) {
    return;
  }

  extend('required', {
    ...required,
    message: 'The {_field_} is required',
  });
  extend('email', {
    ...email,
    message: 'Please enter a correct e-mail address',
  });
  extend('max', {
    ...max,
    params: ['length'],
    message: 'The {_field_} cannot exceed {length} characters',
  });
  extend('sqlAttacks', {
    message() {
      return 'Please enter a valid data';
    },
    validate(value) {
      return !value || !preventSqlAttacks(String(value));
    },
  });

  areValidationRulesRegistered = true;
};

const getBaseInitialForm = (): HydrogenContactForm => ({
  name: '',
  position: '',
  telephone: '',
  email: '',
  company: '',
  comment: '',
});

const getBaseInitialCompanyInfo = (): HydrogenCompanyInfo => ({
  country: '',
  city: '',
  postalCode: '',
});

export const useHydrogenContactForm = (options: HydrogenContactFormOptions) => {
  const { preventXSSAttacks, preventSqlAttacks } = Utils();
  const { submitComment, submitHydrogenLead, submitHydrogenLeadsNewsletterRequest } = useContact();
  const { eventFormSubmit, eventCustomlinksInteracted } = useGtm();
  const { app: { i18n, $recaptcha, $config } } = useContext();
  const getInitialForm = options.getInitialForm || getBaseInitialForm;
  const getInitialCompanyInfo = options.getInitialCompanyInfo || getBaseInitialCompanyInfo;
  const requestTypeOptions = options.requestTypeOptions || [];
  const defaultRequestType = options.defaultRequestType || requestTypeOptions[0] || '';

  registerValidationRules(preventSqlAttacks);

  const isOpenNotification = ref(false);
  const notification = ref({
    message: '',
    type: 'secondary',
  });
  const recaptchaError = ref('');
  const recaptchaToken = ref('');
  const contactForm = ref(null);
  const isSubmitSuccess = ref(false);
  const isSubmitDisabled = ref(false);
  const isSubmitting = ref(false);
  const newsletterEmail = ref('');
  const newsletterMessage = ref('');
  const newsletterMessageType = ref('');
  const isNewsletterSubmitting = ref(false);
  const maxCharacters = ref(options.maxCharacters || 750);
  const leftCharacters = ref(maxCharacters.value);
  const form = ref(getInitialForm());
  const personalInfo = ref<HydrogenPersonalInfo>({
    lastName: '',
  });
  const companyInfo = ref(getInitialCompanyInfo());
  const communicationConsent = ref(false);
  const requestType = ref(defaultRequestType);
  const isRecaptchaEnabled = ref<boolean>(typeof $recaptcha !== 'undefined' && Boolean($config.isRecaptcha));

  const getSubmissionContext = (): HydrogenContactFormContext => ({
    form: form.value,
    personalInfo: personalInfo.value,
    companyInfo: companyInfo.value,
    communicationConsent: communicationConsent.value,
    requestType: requestType.value,
    i18n,
  });

  const scrollToTop = () => {
    const scrollOptions = {
      container: 'body',
      lazy: false,
      offset: 0,
      force: true,
    };
    VueScrollTo.scrollTo('body', 500, scrollOptions);
  };

  const scrollTo = (target: string, offset = 0) => {
    const scrollOptions = {
      container: 'body',
      lazy: false,
      offset,
      force: true,
    };
    VueScrollTo.scrollTo(target, 500, scrollOptions);
  };

  const showMessage = (message, type) => {
    notification.value = {
      message: message as string,
      type,
    };
    isOpenNotification.value = true;
    scrollToTop();
  };

  const clearNotification = () => {
    notification.value = {
      message: '',
      type: 'secondary',
    };
    isOpenNotification.value = false;
  };

  const checkMaxCharacters = () => {
    leftCharacters.value = Math.max(maxCharacters.value - form.value.comment.length, 0);
  };

  const resetRuntimeState = () => {
    form.value = getInitialForm();
    personalInfo.value.lastName = '';
    companyInfo.value = getInitialCompanyInfo();
    if (options.onReset) {
      options.onReset();
    }
    communicationConsent.value = false;
    requestType.value = defaultRequestType;
    leftCharacters.value = maxCharacters.value;
    recaptchaToken.value = '';
    recaptchaError.value = '';
    if (isRecaptchaEnabled.value && !isSubmitSuccess.value) {
      $recaptcha.reset();
    }
  };

  const clearFormState = () => {
    clearNotification();
    resetRuntimeState();
    isSubmitSuccess.value = false;
  };

  const startNewInquiry = () => {
    clearFormState();
    scrollToTop();
  };

  const onSubmit = async () => {
    if (isSubmitting.value) {
      return;
    }

    isSubmitting.value = true;

    try {
      const submissionContext = getSubmissionContext();
      const builtComment = options.buildComment(submissionContext);
      const countryCode = companyInfo.value.country || '';
      const country = getHydrogenCountryLabel(countryCode, i18n.t('China') as string);
      let submitData = assign(clone(form.value), {
        name: trim(join([personalInfo.value.lastName, form.value.name], ' ')),
        comment: builtComment,
        country,
        countryCode,
        subject: options.subject,
      }, options.buildSubmissionData ? options.buildSubmissionData(submissionContext) : {});

      if (isRecaptchaEnabled.value) {
        try {
          $recaptcha.init();
          submitData = assign(submitData, { recaptchaToken: await $recaptcha.getResponse() });
        } catch (err) {
          recaptchaError.value = i18n.t('Validation failed') as string;
          eventFormSubmit({
            formName: options.formName,
            formAction: 'Form Submitted',
            formStatus: 'Form Error',
            formErrorMessages: i18n.t('Validation failed'),
          });
          return;
        }
      }

      const hydrogenLeadPayload = {
        addressLine1: companyInfo.value.addressLine1 || '',
        addressLine2: companyInfo.value.addressLine2 || '',
        city: companyInfo.value.city || '',
        companyName: form.value.company || '',
        country,
        countryCode,
        dynamicTitle: options.dynamicTitle || '',
        email: form.value.email,
        lastName: personalInfo.value.lastName,
        firstName: form.value.name,
        leadSubSource: options.leadSubSource || '',
        message: form.value.comment,
        phone: form.value.telephone || '',
        postalCode: companyInfo.value.postalCode || '',
        ...(communicationConsent.value ? { newsletter: true } : {}),
        ...(options.buildHydrogenLeadData ? options.buildHydrogenLeadData(submissionContext) : {}),
        ...(isRecaptchaEnabled.value ? { recaptchaToken: submitData.recaptchaToken } : {}),
      };

      const result = await (options.dynamicTitle && options.leadSubSource
        ? submitHydrogenLead(hydrogenLeadPayload)
        : submitComment(submitData));
      const resultData = result.data as (ContactUsMutationResult & SendHydrogenLeadsRequestMutationResult) | undefined;

      if (result.errors) {
        showMessage(result.errors[0]?.message as string, 'danger');
        eventFormSubmit({
          formName: options.formName,
          formAction: 'Form Submitted',
          formStatus: 'Form Error',
          formErrorMessages: result.errors[0]?.message,
        });
      } else if (resultData?.contactUs?.status || resultData?.sendHydrogenLeadsRequest?.success) {
        clearFormState();
        if (contactForm.value) {
          (contactForm.value as HTMLFormElement).reset();
        }
        isSubmitSuccess.value = true;
        eventFormSubmit({
          formName: options.formName,
          formAction: 'Form Submitted',
          formStatus: 'Form Success',
        });
        await nextTick();
        scrollTo('.hydrogen-contact-success', -200);
      } else {
        showMessage(i18n.t('Failed to submit') as string, 'danger');
        eventFormSubmit({
          formName: options.formName,
          formAction: 'Form Submitted',
          formStatus: 'Form Error',
          formErrorMessages: i18n.t('Failed to submit'),
        });
      }

      if (isRecaptchaEnabled.value && !isSubmitSuccess.value) {
        $recaptcha.reset();
      }
    } finally {
      isSubmitting.value = false;
    }
  };

  const submitForm = async (handleSubmit: (callback: () => Promise<void>) => Promise<unknown>) => {
    if (isSubmitDisabled.value) {
      return;
    }

    isSubmitDisabled.value = true;
    clearNotification();

    try {
      await handleSubmit(onSubmit);
    } finally {
      isSubmitDisabled.value = false;
    }
  };

  const resetForm = () => {
    clearFormState();
    eventCustomlinksInteracted({
      eventlabel: 'Reset Form',
      eventCategory: options.formName,
    });
  };

  const onNewsletterSubmit = async () => {
    if (isNewsletterSubmitting.value) {
      return;
    }

    isNewsletterSubmitting.value = true;
    newsletterMessage.value = '';
    newsletterMessageType.value = '';

    try {
      const result = await submitHydrogenLeadsNewsletterRequest(newsletterEmail.value);
      const resultData = result.data as SendHydrogenLeadsNewsletterRequestMutationResult | undefined;
      const newsletterResult = resultData?.sendHydrogenLeadsNewsletterRequest;

      if (result.errors) {
        newsletterMessage.value = i18n.t(result.errors[0]?.message || 'Failed to submit') as string;
        newsletterMessageType.value = 'error';
      } else {
        newsletterMessage.value = newsletterResult?.message || (
          newsletterResult?.success || newsletterResult?.status
            ? i18n.t('Thank you for subscribing to our newsletter') as string
            : i18n.t('Failed to submit') as string
        );
        newsletterMessageType.value = newsletterResult?.success || newsletterResult?.status ? 'success' : 'error';
        if (newsletterResult?.success || newsletterResult?.status) {
          newsletterEmail.value = '';
        }
      }
    } catch (err) {
      newsletterMessage.value = i18n.t('Failed to submit') as string;
      newsletterMessageType.value = 'error';
    } finally {
      isNewsletterSubmitting.value = false;
    }
  };

  const onRecaptchaExpired = () => {
    recaptchaError.value = i18n.t('Verification code expired') as string;
    recaptchaToken.value = '';
  };

  const onRecaptchaSuccess = (token) => {
    recaptchaToken.value = token;
    recaptchaError.value = '';
  };

  const telCheck = () => {
    form.value.telephone = form.value.telephone.replace(/[^0-9+\-\s().]/g, '');
  };

  const filterCharacters = (e) => {
    const target = e.target as HTMLInputElement;
    const fieldName = target.name;
    const sanitizedValue = preventXSSAttacks(target.value);

    if (isFormTextFieldName(fieldName)) {
      form.value[fieldName] = sanitizedValue;
    }
    if (Object.prototype.hasOwnProperty.call(personalInfo.value, fieldName)) {
      (personalInfo.value as Record<string, string>)[fieldName] = sanitizedValue;
    }
    if (Object.prototype.hasOwnProperty.call(companyInfo.value, fieldName)) {
      (companyInfo.value as Record<string, string>)[fieldName] = sanitizedValue;
    }
  };

  return {
    checkMaxCharacters,
    clearFormState,
    communicationConsent,
    companyInfo,
    contactForm,
    eventCustomlinksInteracted,
    filterCharacters,
    form,
    isOpenNotification,
    isNewsletterSubmitting,
    isRecaptchaEnabled,
    isSubmitDisabled,
    isSubmitSuccess,
    isSubmitting,
    leftCharacters,
    maxCharacters,
    newsletterEmail,
    newsletterMessage,
    newsletterMessageType,
    notification,
    onNewsletterSubmit,
    onRecaptchaExpired,
    onRecaptchaSuccess,
    onSubmit,
    personalInfo,
    recaptchaError,
    recaptchaToken,
    requestType,
    requestTypeOptions,
    resetForm,
    scrollTo,
    scrollToTop,
    startNewInquiry,
    submitForm,
    telCheck,
  };
};

export default useHydrogenContactForm;
